import { useRef } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { ImagePlusIcon, XIcon } from 'lucide-react'

interface Props {
  images: string[]
  onChange: (images: string[]) => void
  maxImages?: number
  maxFileSizeMB?: number
  disabled?: boolean
  onFilesRejected?: (count: number) => void
}

export function ReferenceImageAttacher({
  images,
  onChange,
  maxImages = 8,
  maxFileSizeMB = 10,
  disabled,
  onFilesRejected,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFiles = async (files: FileList) => {
    const remaining = maxImages - images.length
    if (remaining <= 0) return

    const maxBytes = maxFileSizeMB * 1024 * 1024
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'))
    const validFiles = imageFiles.filter((f) => f.size <= maxBytes)
    const rejectedCount = imageFiles.length - validFiles.length
    if (rejectedCount > 0) onFilesRejected?.(rejectedCount)

    const filesToProcess = validFiles.slice(0, remaining)

    const results = await Promise.all(
      filesToProcess.map(
        (file) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(file)
          }),
      ),
    )

    onChange([...images, ...results])
  }

  const handleRemove = (index: number) => {
    onChange(images.filter((_, i) => i !== index))
  }

  return (
    <div className="flex items-center gap-1.5">
      {images.map((img, i) => (
        <div key={img.slice(-20)} className="group relative h-8 w-8 shrink-0">
          <img
            src={img}
            alt={`Reference ${i + 1}`}
            className="h-full w-full rounded object-cover"
          />
          <button
            type="button"
            onClick={() => handleRemove(i)}
            disabled={disabled}
            className="bg-background/80 absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full group-hover:flex">
            <XIcon className="h-2.5 w-2.5" />
          </button>
        </div>
      ))}

      {images.length < maxImages && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}>
            <ImagePlusIcon className="h-4 w-4" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files).catch(() => {})
              e.target.value = ''
            }}
          />
        </>
      )}
    </div>
  )
}
